/**
 * DP / Folha — Fase 2.1: Cadastro Trabalhista Expandido (eSocial-ready)
 * Aba da página DPFolhaConfigPage
 *
 * Funcionalidades:
 *  - Lista todos os colaboradores com indicador de completude DP
 *  - Filtro por busca + opção "somente pendentes"
 *  - Drawer/Dialog de edição com formulário eSocial completo
 *  - Seletor CBO com busca tipo-ahead
 *  - Gestão de dependentes IRRF/SF
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Pencil, Search, Save, Plus, Trash2, User, CheckCircle2, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { formatCPF, validateDateBR } from '../../lib/helpers';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ============================================================================
// SeletorCBO — busca tipo-ahead reutilizável
// ============================================================================
const SeletorCBO = ({ value, onChange, testid = 'cbo-search' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentDesc, setCurrentDesc] = useState('');
  const timer = useRef(null);

  // Carrega descrição quando "value" inicial é setado
  useEffect(() => {
    const loadCurrent = async () => {
      if (!value) { setCurrentDesc(''); return; }
      try {
        const r = await axios.get(`${API}/api/calculos-dp/ref/cbo?q=${value}&limit=1`, authHeader());
        const found = (r.data || []).find(c => c.codigo === value);
        setCurrentDesc(found?.descricao || '');
      } catch (e) { /* ignore */ }
    };
    loadCurrent();
  }, [value]);

  const search = (q) => {
    if (timer.current) clearTimeout(timer.current);
    setQuery(q);
    if (!q || q.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/calculos-dp/ref/cbo?q=${encodeURIComponent(q)}&limit=20`, authHeader());
        setResults(r.data || []);
      } catch (e) {
        toast.error('Erro na busca CBO');
      } finally { setLoading(false); }
    }, 300);
  };

  const pick = (cbo) => {
    onChange(cbo.codigo);
    setCurrentDesc(cbo.descricao);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <Input
          placeholder="Buscar CBO (ex.: analista, gerente, 2124...)"
          value={query}
          onChange={(e) => { search(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={testid}
        />
        {value && (
          <Button type="button" size="sm" variant="ghost" onClick={() => { onChange(''); setCurrentDesc(''); }}>
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        )}
      </div>
      {value && (
        <div className="mt-1 text-xs text-slate-600">
          <span className="font-mono font-semibold">{value}</span> — {currentDesc || <span className="italic">carregando...</span>}
        </div>
      )}
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg">
          {loading && <div className="p-2 text-xs text-slate-500"><Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Buscando...</div>}
          {results.map((c) => (
            <button
              key={c.codigo}
              type="button"
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b last:border-0"
              data-testid={`cbo-option-${c.codigo}`}
            >
              <span className="font-mono font-semibold text-indigo-700">{c.codigo}</span>
              {' — '}
              <span>{c.descricao}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// EditorDPData — formulário completo eSocial-ready
// ============================================================================
const EditorDPData = ({ employee, dpData, refs, onSave, onClose }) => {
  const empty = {
    tipo_vinculo_esocial: '',
    categoria_esocial: '',
    cbo_codigo: '',
    regime_jornada: '',
    regime_trabalhista: '1',
    natureza_atividade: '1',
    centro_custo_id: '',
    sindicato_id: '',
    convencao_coletiva_id: '',
    optante_sindicato: true,
    nivel_estagio: '',
    escola_nome: '',
    agente_integracao_nome: '',
    curso_nome: '',
    entidade_qualificadora_nome: '',
    aprendiz_carga_horaria_curso: '',
    multiplos_vinculos: false,
    valor_outros_vinculos: 0,
    multiplos_vinculos_observacao: '',
    data_opcao_fgts: '',
    aposentado_invalidez: false,
    regime_previdenciario: 'RGPS',
    inscricao_inss: '',
    dependentes_irrf: [],
    dependentes_sf: [],
  };
  const [form, setForm] = useState(() => {
    // Normaliza null/undefined dos campos string para '' para evitar warning React 'value should not be null'
    const merged = { ...empty, ...dpData };
    for (const k of Object.keys(empty)) {
      if (typeof empty[k] === 'string' && merged[k] == null) merged[k] = '';
    }
    return merged;
  });
  const [saving, setSaving] = useState(false);
  const convencaoSelecionada = refs.convencoes.find((item) => item.id === form.convencao_coletiva_id);
  const sindicatoDerivado = convencaoSelecionada?.sindicato_laboral;

  useEffect(() => {
    if (!convencaoSelecionada?.sindicato_laboral_id) return;
    if (form.sindicato_id === convencaoSelecionada.sindicato_laboral_id) return;
    setForm((prev) => ({ ...prev, sindicato_id: convencaoSelecionada.sindicato_laboral_id }));
  }, [convencaoSelecionada, form.sindicato_id]);

  const update = (k, v) => setForm({ ...form, [k]: v });
  const isEstagiario = form.tipo_vinculo_esocial === '801';
  const isAprendiz = form.tipo_vinculo_esocial === '103';

  const addDepIRRF = () => update('dependentes_irrf', [...form.dependentes_irrf, {
    id: `tmp-${Date.now()}`, nome: '', cpf: '', data_nascimento: '', tipo: '03', inicio_dependencia: new Date().toISOString().slice(0, 10),
  }]);
  const updDepIRRF = (i, k, v) => {
    const arr = [...form.dependentes_irrf];
    arr[i] = { ...arr[i], [k]: v };
    update('dependentes_irrf', arr);
  };
  const delDepIRRF = (i) => update('dependentes_irrf', form.dependentes_irrf.filter((_, idx) => idx !== i));

  const addDepSF = () => update('dependentes_sf', [...form.dependentes_sf, {
    id: `tmp-${Date.now()}`, nome: '', cpf: '', data_nascimento: '', documentacao_comprobatoria: '',
  }]);
  const updDepSF = (i, k, v) => {
    const arr = [...form.dependentes_sf];
    arr[i] = { ...arr[i], [k]: v };
    update('dependentes_sf', arr);
  };
  const delDepSF = (i) => update('dependentes_sf', form.dependentes_sf.filter((_, idx) => idx !== i));

  const save = async () => {
    if (form.data_opcao_fgts && !validateDateBR(form.data_opcao_fgts, { futureYears: 0, pastYears: 120 })) {
      toast.error('Data de opção do FGTS inválida');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        aprendiz_carga_horaria_curso: form.aprendiz_carga_horaria_curso ? parseFloat(form.aprendiz_carga_horaria_curso) : null,
        valor_outros_vinculos: parseFloat(form.valor_outros_vinculos) || 0,
        centro_custo_id: form.centro_custo_id || null,
        sindicato_id: form.sindicato_id || null,
        convencao_coletiva_id: form.convencao_coletiva_id || null,
        optante_sindicato: !!form.optante_sindicato,
      };
      // Remove tmp- ids antes de enviar (backend gera novos)
      payload.dependentes_irrf = payload.dependentes_irrf.map(({ id, ...rest }) => (id?.startsWith('tmp-') ? rest : { id, ...rest }));
      payload.dependentes_sf = payload.dependentes_sf.map(({ id, ...rest }) => (id?.startsWith('tmp-') ? rest : { id, ...rest }));

      await axios.put(`${API}/api/calculos-dp/employees/${employee.id}/dp-data`, payload, authHeader());
      toast.success('Dados DP salvos');
      onSave?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Identificação */}
      <Card>
        <CardContent className="p-3 bg-slate-50 flex items-center gap-3">
          <User className="h-5 w-5 text-indigo-600" />
          <div className="flex-1">
            <div className="font-semibold">{employee.nome}</div>
            <div className="text-xs text-slate-600">
              {employee.cargo} · {employee.filiais} · admitido em {employee.admissao} · regime {employee.regime}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="vinculo" className="w-full">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="vinculo">Vínculo eSocial</TabsTrigger>
          <TabsTrigger value="vinculos-dp">Vínculos DP</TabsTrigger>
          <TabsTrigger value="estag-aprend">Estágio/Aprendiz</TabsTrigger>
          <TabsTrigger value="dependentes">Dependentes</TabsTrigger>
        </TabsList>

        {/* ============================ ABA: VÍNCULO eSOCIAL ============================ */}
        <TabsContent value="vinculo" className="space-y-3 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de Vínculo eSocial *</Label>
              <Select value={form.tipo_vinculo_esocial} onValueChange={(v) => update('tipo_vinculo_esocial', v)}>
                <SelectTrigger data-testid="dp-form-tipo-vinculo"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {refs.tiposVinculo.map((c) => (
                    <SelectItem key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoria eSocial</Label>
              <Select value={form.categoria_esocial} onValueChange={(v) => update('categoria_esocial', v)}>
                <SelectTrigger data-testid="dp-form-categoria"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {refs.categorias.map((c) => (
                    <SelectItem key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>CBO (Classificação Brasileira de Ocupações) *</Label>
              <SeletorCBO value={form.cbo_codigo} onChange={(v) => update('cbo_codigo', v)} testid="dp-form-cbo" />
            </div>
            <div>
              <Label>Regime de Jornada</Label>
              <Select value={form.regime_jornada} onValueChange={(v) => update('regime_jornada', v)}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Submetido a horário (CLT padrão)</SelectItem>
                  <SelectItem value="2">2 — Atividade externa (Art. 62 CLT)</SelectItem>
                  <SelectItem value="3">3 — Cargo de gestão / confiança</SelectItem>
                  <SelectItem value="9">9 — Empregado doméstico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Regime Trabalhista</Label>
              <Select value={form.regime_trabalhista} onValueChange={(v) => update('regime_trabalhista', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — CLT</SelectItem>
                  <SelectItem value="2">2 — Estatutário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Natureza da Atividade</Label>
              <Select value={form.natureza_atividade} onValueChange={(v) => update('natureza_atividade', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Urbana</SelectItem>
                  <SelectItem value="2">2 — Rural</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>NIS/PIS/PASEP (inscrição INSS)</Label>
              <Input value={form.inscricao_inss} onChange={(e) => update('inscricao_inss', e.target.value)} />
            </div>
            <div>
              <Label>Data Opção FGTS</Label>
              <Input type="date" value={form.data_opcao_fgts || ''} onChange={(e) => update('data_opcao_fgts', e.target.value)} />
            </div>
            <div>
              <Label>Regime Previdenciário</Label>
              <Select value={form.regime_previdenciario || 'RGPS'} onValueChange={(v) => update('regime_previdenciario', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="RGPS">RGPS — Regime Geral (INSS)</SelectItem>
                  <SelectItem value="RPPS">RPPS — Regime Próprio (servidor público)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card className="bg-amber-50/50 border-amber-200">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Múltiplos Vínculos</Label>
                  <p className="text-xs text-slate-600">Colaborador possui outros empregos → impacta no cálculo do INSS-teto agregado.</p>
                </div>
                <Switch checked={form.multiplos_vinculos} onCheckedChange={(v) => update('multiplos_vinculos', v)} data-testid="dp-form-mult-vinc" />
              </div>
              {form.multiplos_vinculos && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Valor dos outros vínculos (R$)</Label>
                    <CurrencyInput value={form.valor_outros_vinculos} onChange={v => update('valor_outros_vinculos', v)} />
                  </div>
                  <div>
                    <Label>Observação</Label>
                    <Input value={form.multiplos_vinculos_observacao} onChange={(e) => update('multiplos_vinculos_observacao', e.target.value)} />
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Aposentado por Invalidez</Label>
                  <p className="text-xs text-slate-600">Quando ativo, não desconta INSS (regra especial).</p>
                </div>
                <Switch checked={form.aposentado_invalidez} onCheckedChange={(v) => update('aposentado_invalidez', v)} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================ ABA: VÍNCULOS DP ============================ */}
        <TabsContent value="vinculos-dp" className="space-y-3 mt-4">
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>Centro de Custo</Label>
              <Select value={form.centro_custo_id} onValueChange={(v) => update('centro_custo_id', v)}>
                <SelectTrigger data-testid="dp-form-cc"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {refs.centrosCusto.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.codigo} — {c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sindicato (derivado da convenção)</Label>
              <Input
                value={
                  sindicatoDerivado
                    ? `${sindicatoDerivado.nome}${sindicatoDerivado.sigla ? ` (${sindicatoDerivado.sigla})` : ''}`
                    : ''
                }
                placeholder="Selecione uma convenção coletiva"
                disabled
              />
              <p className="text-xs text-slate-500 mt-1">
                O sindicato laboral passa a ser definido automaticamente pela convenção coletiva selecionada.
              </p>
            </div>
            <div>
              <Label>Convenção Coletiva (CCT)</Label>
              <Select value={form.convencao_coletiva_id} onValueChange={(v) => update('convencao_coletiva_id', v)}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {refs.convencoes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        {/* ============================ ABA: ESTÁGIO/APRENDIZ ============================ */}
        <TabsContent value="estag-aprend" className="space-y-3 mt-4">
          {!isEstagiario && !isAprendiz && (
            <p className="text-sm text-slate-500 italic">Esta aba só se aplica quando o Tipo de Vínculo é "Aprendiz (103)" ou "Estagiário (801)".</p>
          )}
          {isAprendiz && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Entidade Qualificadora</Label>
                <Input value={form.entidade_qualificadora_nome} onChange={(e) => update('entidade_qualificadora_nome', e.target.value)} placeholder="Ex.: SENAI, SENAC..." />
              </div>
              <div>
                <Label>Carga Horária do Curso</Label>
                <Input type="number" value={form.aprendiz_carga_horaria_curso} onChange={(e) => update('aprendiz_carga_horaria_curso', e.target.value)} placeholder="Horas totais" />
              </div>
            </div>
          )}
          {isEstagiario && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nível</Label>
                <Select value={form.nivel_estagio} onValueChange={(v) => update('nivel_estagio', v)}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="superior">Superior</SelectItem>
                    <SelectItem value="medio">Médio / Técnico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Curso</Label>
                <Input value={form.curso_nome} onChange={(e) => update('curso_nome', e.target.value)} />
              </div>
              <div>
                <Label>Escola / Instituição</Label>
                <Input value={form.escola_nome} onChange={(e) => update('escola_nome', e.target.value)} />
              </div>
              <div>
                <Label>Agente de Integração</Label>
                <Input value={form.agente_integracao_nome} onChange={(e) => update('agente_integracao_nome', e.target.value)} placeholder="Ex.: CIEE, IEL, NUBE..." />
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============================ ABA: DEPENDENTES ============================ */}
        <TabsContent value="dependentes" className="space-y-4 mt-4">
          {/* IRRF */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-base">Dependentes para IRRF</Label>
                <p className="text-xs text-slate-500">Reduzem a base de cálculo do imposto de renda.</p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addDepIRRF} data-testid="dp-add-dep-irrf">
                <Plus className="h-3 w-3 mr-1" /> Adicionar
              </Button>
            </div>
            {form.dependentes_irrf.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Nenhum dependente IRRF cadastrado.</p>
            ) : (
              <div className="space-y-2">
                {form.dependentes_irrf.map((d, i) => (
                  <div key={d.id || i} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-slate-50">
                    <div className="col-span-4">
                      <Label className="text-xs">Nome</Label>
                      <Input className="h-9" value={d.nome} onChange={(e) => updDepIRRF(i, 'nome', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">CPF</Label>
                      <Input className="h-9" value={d.cpf || ''} onChange={(e) => updDepIRRF(i, 'cpf', formatCPF(e.target.value))} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Nasc.</Label>
                      <Input className="h-9" type="date" value={d.data_nascimento} onChange={(e) => updDepIRRF(i, 'data_nascimento', e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Tipo</Label>
                      <Select value={d.tipo} onValueChange={(v) => updDepIRRF(i, 'tipo', v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {refs.tiposDepIRRF.map((t) => (
                            <SelectItem key={t.codigo} value={t.codigo}>{t.codigo} — {t.descricao.slice(0, 40)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => delDepIRRF(i)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SF */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-base">Dependentes para Salário-Família</Label>
                <p className="text-xs text-slate-500">Filhos &lt; 14 anos. Pago pela Previdência conforme faixa de renda.</p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addDepSF} data-testid="dp-add-dep-sf">
                <Plus className="h-3 w-3 mr-1" /> Adicionar
              </Button>
            </div>
            {form.dependentes_sf.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Nenhum dependente para salário-família cadastrado.</p>
            ) : (
              <div className="space-y-2">
                {form.dependentes_sf.map((d, i) => (
                  <div key={d.id || i} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-slate-50">
                    <div className="col-span-4">
                      <Label className="text-xs">Nome</Label>
                      <Input className="h-9" value={d.nome} onChange={(e) => updDepSF(i, 'nome', e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">CPF</Label>
                      <Input className="h-9" value={d.cpf || ''} onChange={(e) => updDepSF(i, 'cpf', formatCPF(e.target.value))} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Nasc.</Label>
                      <Input className="h-9" type="date" value={d.data_nascimento} onChange={(e) => updDepSF(i, 'data_nascimento', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Documentação</Label>
                      <Input className="h-9" value={d.documentacao_comprobatoria || ''} onChange={(e) => updDepSF(i, 'documentacao_comprobatoria', e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => delDepSF(i)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={save} disabled={saving} data-testid="dp-form-salvar-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar
        </Button>
      </DialogFooter>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL — Lista + Editor
// ============================================================================
const CadastroTrabalhistaTab = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [somentePendentes, setSomentePendentes] = useState(false);
  const [editing, setEditing] = useState(null); // { employee, dp_data }
  const [refs, setRefs] = useState({
    tiposVinculo: [], categorias: [], centrosCusto: [], sindicatos: [], convencoes: [], tiposDepIRRF: [],
  });

  const loadRefs = useCallback(async () => {
    try {
      const [tv, cat, cc, sind, cct, depIrrf] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/ref/tipos-vinculo-esocial`, authHeader()),
        axios.get(`${API}/api/calculos-dp/ref/categorias-esocial`, authHeader()),
        axios.get(`${API}/api/calculos-dp/centros-custo`, authHeader()),
        axios.get(`${API}/api/calculos-dp/sindicatos`, authHeader()),
        axios.get(`${API}/api/calculos-dp/convencoes`, authHeader()),
        axios.get(`${API}/api/calculos-dp/ref/tipos-dependente-irrf`, authHeader()),
      ]);
      setRefs({
        tiposVinculo: tv.data || [],
        categorias: cat.data || [],
        centrosCusto: cc.data || [],
        sindicatos: sind.data || [],
        convencoes: cct.data || [],
        tiposDepIRRF: depIrrf.data || [],
      });
    } catch (e) {
      toast.error('Erro ao carregar referências');
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtro) params.set('q', filtro);
      if (somentePendentes) params.set('somente_pendentes', 'true');
      const r = await axios.get(`${API}/api/calculos-dp/employees/dp-status?${params}`, authHeader());
      setEmployees(r.data || []);
    } catch (e) {
      toast.error('Erro ao carregar colaboradores');
    } finally { setLoading(false); }
  }, [filtro, somentePendentes]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => {
    const t = setTimeout(loadEmployees, 300);
    return () => clearTimeout(t);
  }, [loadEmployees]);

  const openEditor = async (emp) => {
    try {
      const r = await axios.get(`${API}/api/calculos-dp/employees/${emp.id}/dp-data`, authHeader());
      setEditing(r.data);
    } catch (e) {
      toast.error('Erro ao carregar dados do colaborador');
    }
  };

  const completudeBadge = (pct) => {
    if (pct === 100) return <Badge className="bg-emerald-500 gap-1"><CheckCircle2 className="h-3 w-3" />Completo</Badge>;
    if (pct >= 50) return <Badge className="bg-amber-500 gap-1"><AlertCircle className="h-3 w-3" />{pct}%</Badge>;
    return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />Pendente</Badge>;
  };

  // ============ Auto-fill em massa ============
  const [autoFillOpen, setAutoFillOpen] = useState(false);
  const [autoFillData, setAutoFillData] = useState(null);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [autoFillSelected, setAutoFillSelected] = useState(new Set());
  const [autoFillApplying, setAutoFillApplying] = useState(false);

  const openAutoFill = async () => {
    setAutoFillOpen(true);
    setAutoFillLoading(true);
    setAutoFillData(null);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/employees/auto-fill-preview?somente_pendentes=true`, authHeader());
      setAutoFillData(r.data);
      setAutoFillSelected(new Set(r.data.sugestoes.map(s => s.employee.id)));
    } catch (e) {
      toast.error('Erro ao gerar preview');
    } finally { setAutoFillLoading(false); }
  };

  const toggleAutoFillRow = (id) => {
    const next = new Set(autoFillSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setAutoFillSelected(next);
  };

  const applyAutoFill = async () => {
    if (autoFillSelected.size === 0) return toast.error('Selecione ao menos um colaborador');
    setAutoFillApplying(true);
    try {
      const r = await axios.post(
        `${API}/api/calculos-dp/employees/auto-fill-apply`,
        { employee_ids: Array.from(autoFillSelected), sobrescrever: false },
        authHeader()
      );
      toast.success(`✅ ${r.data.aplicados.length} colaboradores preenchidos automaticamente`);
      setAutoFillOpen(false);
      setAutoFillData(null);
      loadEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao aplicar');
    } finally { setAutoFillApplying(false); }
  };

  const totalCompletos = employees.filter(e => e.completude_dp === 100).length;
  const totalPendentes = employees.length - totalCompletos;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500 uppercase">Total Colaboradores</div>
          <div className="text-2xl font-bold">{employees.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-emerald-700 uppercase">Cadastro Completo</div>
          <div className="text-2xl font-bold text-emerald-700">{totalCompletos}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-amber-700 uppercase">Pendentes</div>
          <div className="text-2xl font-bold text-amber-700">{totalPendentes}</div>
        </CardContent></Card>
      </div>

      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Buscar por nome ou cargo..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="pl-9"
            data-testid="dp-trab-busca"
          />
        </div>
        <Button
          variant={somentePendentes ? 'default' : 'outline'}
          onClick={() => setSomentePendentes(!somentePendentes)}
          data-testid="dp-trab-pendentes-btn"
        >
          {somentePendentes ? '✓ Somente Pendentes' : 'Somente Pendentes'}
        </Button>
        <Button
          variant="outline"
          onClick={openAutoFill}
          disabled={totalPendentes === 0}
          data-testid="dp-trab-autofill-btn"
          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
        >
          <Sparkles className="h-4 w-4 mr-2" /> Auto-preencher Pendentes ({totalPendentes})
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Colaborador</th>
              <th className="text-left p-3">Cargo / Filial</th>
              <th className="text-left p-3">Regime</th>
              <th className="text-left p-3">Tipo Vínculo</th>
              <th className="text-left p-3">CBO</th>
              <th className="text-center p-3">Status DP</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-slate-500 text-center"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Carregando...</td></tr>
            ) : employees.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-slate-500 text-center">Nenhum colaborador encontrado.</td></tr>
            ) : employees.map((e) => (
              <tr key={e.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{e.nome}</td>
                <td className="p-3 text-xs text-slate-600">{e.cargo || '—'} <br />{e.filiais || '—'}</td>
                <td className="p-3"><Badge variant="outline">{e.regime || '—'}</Badge></td>
                <td className="p-3 font-mono text-xs">{e.tipo_vinculo_esocial || '—'}</td>
                <td className="p-3 font-mono text-xs">{e.cbo_codigo || '—'}</td>
                <td className="p-3 text-center">{completudeBadge(e.completude_dp)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => openEditor(e)} data-testid={`dp-trab-edit-${e.id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Editar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dp-trab-dialog">
          <DialogHeader>
            <DialogTitle>Cadastro Trabalhista — Vínculo eSocial</DialogTitle>
            <DialogDescription>
              Dados de Departamento Pessoal eSocial-ready. Campos críticos: Tipo de Vínculo, Categoria e CBO. Demais campos são opcionais, mas recomendados para emissão correta de eventos.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <EditorDPData
              employee={editing.employee}
              dpData={editing.dp_data}
              refs={refs}
              onSave={() => { setEditing(null); loadEmployees(); }}
              onClose={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog AUTO-FILL */}
      <Dialog open={autoFillOpen} onOpenChange={(o) => !o && setAutoFillOpen(false)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dp-autofill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              Auto-preenchimento Inteligente — Preview
            </DialogTitle>
            <DialogDescription>
              Sugestões baseadas em regras heurísticas (regime + cargo). Revise antes de aplicar. Você poderá editar os dados de cada colaborador manualmente após o preenchimento.
            </DialogDescription>
          </DialogHeader>
          {autoFillLoading ? (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="h-8 w-8 inline animate-spin" />
              <p className="mt-2">Analisando colaboradores...</p>
            </div>
          ) : autoFillData ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="p-3 text-center">
                  <div className="text-xs text-slate-500 uppercase">Pendentes</div>
                  <div className="text-2xl font-bold">{autoFillData.total_pendentes}</div>
                </CardContent></Card>
                <Card><CardContent className="p-3 text-center bg-indigo-50">
                  <div className="text-xs text-indigo-700 uppercase">Selecionados</div>
                  <div className="text-2xl font-bold text-indigo-700">{autoFillSelected.size}</div>
                </CardContent></Card>
                <Card><CardContent className="p-3 text-center">
                  <div className="text-xs text-slate-500 uppercase">Total Coloboradores</div>
                  <div className="text-2xl font-bold">{autoFillData.total_colaboradores}</div>
                </CardContent></Card>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setAutoFillSelected(new Set(autoFillData.sugestoes.map(s => s.employee.id)))}
                  className="text-indigo-700 hover:underline"
                >Selecionar todos</button>
                <span className="text-slate-400">·</span>
                <button
                  type="button"
                  onClick={() => setAutoFillSelected(new Set())}
                  className="text-slate-700 hover:underline"
                >Desmarcar todos</button>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className="text-left p-2">Colaborador / Cargo</th>
                      <th className="text-left p-2">Regime</th>
                      <th className="text-left p-2">Vínculo eSocial</th>
                      <th className="text-left p-2">CBO sugerido</th>
                      <th className="text-left p-2">Justificativa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoFillData.sugestoes.map((s) => {
                      const e = s.employee;
                      const sg = s.sugestao;
                      const checked = autoFillSelected.has(e.id);
                      return (
                        <tr
                          key={e.id}
                          className={`border-t ${checked ? 'bg-indigo-50/30' : 'bg-white'} cursor-pointer hover:bg-indigo-50/50`}
                          onClick={() => toggleAutoFillRow(e.id)}
                          data-testid={`dp-autofill-row-${e.id}`}
                        >
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAutoFillRow(e.id)}
                              className="h-4 w-4"
                            />
                          </td>
                          <td className="p-2">
                            <div className="font-medium">{e.nome}</div>
                            <div className="text-xs text-slate-500">{e.cargo}</div>
                          </td>
                          <td className="p-2"><Badge variant="outline">{e.regime}</Badge></td>
                          <td className="p-2 font-mono text-xs">{sg.tipo_vinculo_esocial} / {sg.categoria_esocial}</td>
                          <td className="p-2">
                            {sg.cbo_codigo ? (
                              <div>
                                <span className="font-mono font-semibold">{sg.cbo_codigo}</span>
                                <div className="text-xs text-slate-500">{sg.cbo_descricao}</div>
                              </div>
                            ) : (
                              <Badge variant="destructive" className="text-xs">CBO não inferido</Badge>
                            )}
                          </td>
                          <td className="p-2 text-xs text-slate-600 max-w-xs">
                            <ul className="list-disc list-inside space-y-0.5">
                              {sg.razao.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoFillOpen(false)} disabled={autoFillApplying}>
              Cancelar
            </Button>
            <Button
              onClick={applyAutoFill}
              disabled={autoFillApplying || autoFillSelected.size === 0}
              data-testid="dp-autofill-apply-btn"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {autoFillApplying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Aplicar a {autoFillSelected.size} colaborador(es)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CadastroTrabalhistaTab;
